import { Fragment, useState, useMemo, useRef, useEffect } from "react";
import {
  AVAILABLE_TILES,
  DEFAULT_TILE,
  MAP_MAX_ZOOM,
  TILE_CONFIG,
  Tiles,
} from "../../global/constants/tiles";
import {
  MapContainer,
  GeoJSON as LeafletGeoJSON,
  TileLayer,
  useMap,
} from "react-leaflet";
import {
  area as turfArea,
  bbox as turfBbox,
  buffer as turfBuffer,
  union as turfUnion,
  difference as turfDifference,
  featureCollection as turfFc,
} from "@turf/turf";
import {
  Wind,
  Sun,
  Trash2,
  Upload,
  Loader2,
  Layers,
  ChevronLeft,
  ChevronRight,
  FileText,
} from "lucide-react";
import L from "leaflet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { domToPng } from "modern-screenshot";
import { Footer } from "../../Components";
import { apiService } from "../../services/api";
import { createPropitiousAreaJob } from "../../services/propitiousAreaJob";
import { useRulerModalContext } from "../../context/RulerContext";
import { useLayerPermissions } from "../../hooks/useLayerPermissions";
import type { Drawing } from "../../context/RulerContext";
import { ProjecaoReportPreview } from "./report/ProjecaoReportPreview";
import type { ProjecaoReportData } from "./report/types";
import {
  PageWrapper,
  Sidebar,
  SidebarInner,
  SidebarToggle,
  SidebarHeader,
  SidebarSection,
  SectionTitle,
  MapWrapper,
  FilterBlock,
  FilterHeader,
  FilterControls,
  FilterRow,
  FilterSelect,
  FilterInput,
  FilterUnit,
  FilterHint,
  ApplyButton,
} from "./styles";

const GEOSERVER_WFS =
  "https://geoserver-atlas.isitics.com/geoserver/pepe-atlas/ows";

// ---------------------------------------------------------------------------
// Basemap options
// ---------------------------------------------------------------------------

// Mesmos mapas base do mapa principal (global/constants/tiles) — a URL antiga do
// CartoDB passou a exigir API key e exibia o aviso no lugar das tiles.
const BASEMAPS = AVAILABLE_TILES.map((tile) => ({
  id: tile,
  label: TILE_CONFIG[tile].name,
  url: tile,
  maxNativeZoom: TILE_CONFIG[tile].maxNativeZoom,
  attribution: TILE_CONFIG[tile].attribution,
}));

type BasemapId = Tiles;

function BasemapControl({
  selected,
  onChange,
}: {
  selected: BasemapId;
  onChange: (id: BasemapId) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 24,
        right: 12,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 6,
      }}
    >
      {open && (
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
            padding: "6px 4px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 120,
          }}
        >
          {BASEMAPS.map((b) => (
            <button
              key={b.id}
              onClick={() => {
                onChange(b.id);
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "7px 12px",
                fontSize: 12,
                fontWeight: selected === b.id ? 700 : 400,
                color: selected === b.id ? "#0C2856" : "#374151",
                background: selected === b.id ? "#eff6ff" : "transparent",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        title="Mudar basemap"
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: "#fff",
          border: "1px solid #d1d5db",
          boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: open ? "#0C2856" : "#6b7280",
        }}
      >
        <Layers size={18} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EnergyType = "eolica" | "solar";
type Operator = "menor" | "maior" | "entre";

interface MaskFilter {
  enabled: boolean;
  operator: Operator;
  value: number;
  valueMax: number;
}

interface FilterConfig {
  label: string;
  unit: string;
  hint: string;
  workspace: string | null;
  layerName: string | null;
  elevation?: number;
  step: number;
}

interface InfraLayer {
  layerName: string;
  title: string;
  color: string;
  visible: boolean;
  minDistKm: number;
  maxDistKm: number;
  geojson: GeoJSON.FeatureCollection | null;
  ringGeom: GeoJSON.Feature | null; // diff(buffer(max), buffer(min)) — the propitious corridor
  // Bbox do último fetch — camadas ocultas não acompanham o mapa, então religar
  // com o viewport mudado precisa refazer a busca em vez de reusar o geojson
  fetchedBboxKey: string | null;
  // Bumped on every refetch — react-leaflet's GeoJSON never re-applies `data`
  // to an existing layer, so the render keys must change to remount it
  version: number;
  loading: boolean;
  error: string | null;
}

interface VectorMask {
  id: string;
  layerName: string;
  layerTitle: string;
  color: string;
  bufferKm: number;
  inside: boolean;
  rawGeometry: GeoJSON.FeatureCollection | null;
  effectiveGeometry: GeoJSON.Feature | null;
  loading: boolean;
  error: string | null;
}

interface KmzMask {
  id: string;
  fileName: string;
  color: string;
  bufferKm: number;
  inside: boolean;
  rawGeometry: GeoJSON.FeatureCollection;
  effectiveGeometry: GeoJSON.Feature | null;
}

interface FinancialAnalysis {
  annual_savings_rs: number;
  self_consumption_fraction_pct: number;
  co2_avoided_kg_year: number;
  payback_years: number | null;
  payback_months: number | null;
  irr_pct: number | null;
  lcoe_rs_kwh: number | null;
  cash_flow_cumulative_rs: number;
}

interface CalcResult {
  total_area_km2: number;
  excluded_raster_km2: number;
  excluded_vector_km2: number;
  propitious_area_km2: number;
  installable_gw: number;
  annual_energy_gwh: number;
  capacity_factor?: number | null;
  financial?: FinancialAnalysis | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OPERATOR_LABELS: Record<Operator, string> = {
  menor: "Menor que",
  maior: "Maior que",
  entre: "Entre",
};

const FILTER_CONFIG: Record<string, FilterConfig> = {
  vento: {
    label: "Velocidade do Vento",
    unit: "m/s",
    hint: "Referência: potencial eólico exclui áreas com velocidade < 6 m/s",
    workspace: "pepe-raster",
    layerName: "windspeedtest",
    elevation: 50,
    step: 0.5,
  },
  declividade: {
    label: "Declividade",
    unit: "°",
    hint: "",
    workspace: null,
    layerName: null,
    step: 1,
  },
  ghi: {
    label: "Irradiação Solar (GHI)",
    unit: "kWh/m²/dia",
    hint: "Referência: potencial solar técnico exclui áreas com GHI < 5,8",
    workspace: "aeroespacial",
    layerName: "solar_ghi_200m_sirgas2000_anual",
    step: 0.1,
  },
};

const DECLIV_HINT: Record<EnergyType, string> = {
  eolica: "Referência: potencial eólico exclui terrenos com inclinação > 17°",
  solar: "Referência: potencial solar exclui terrenos com inclinação > 8°",
};
const DECLIV_HINT_COMBINED =
  "Referência: eólico exclui inclinação > 17°, solar exclui > 8°";

const DECLIV_DEFAULT: Record<EnergyType, number> = { eolica: 17, solar: 8 };

/** Filtro raster que só faz sentido enquanto a energia correspondente está
 * adicionada — removê-la tem de restaurar o filtro, senão o valor fica ligado
 * e invisível (a linha some da UI) e continua indo no payload. */
const RASTER_FILTER_BY_ENERGY: Record<EnergyType, string> = {
  eolica: "vento",
  solar: "ghi",
};

const ENERGY_TYPE_LABELS: Record<EnergyType, string> = {
  eolica: "Eólica",
  solar: "Solar",
};

const INITIAL_FILTERS: Record<string, MaskFilter> = {
  vento: { enabled: false, operator: "menor", value: 6, valueMax: 10 },
  declividade: { enabled: false, operator: "maior", value: 17, valueMax: 30 },
  ghi: { enabled: false, operator: "menor", value: 5.8, valueMax: 7 },
};

const TURBINE_OPTIONS = [
  {
    value: "E126",
    label: "Enercon E126-7,5 MW",
    detail: "33,6 MW/km² — Geográfico/Teórico",
  },
  {
    value: "AW125",
    label: "Acciona AW125-3,0 MW",
    detail: "2,4 MW/km² — Econômico I",
  },
  {
    value: "generic",
    label: "Genérico",
    detail: "5 MW/km² — estimativa simplificada",
  },
] as const;

/** Curated list of vector layers available as exclusion masks. */
const MASKABLE_LAYERS = [
  {
    layerName: "unidades_conservacao_ambiental",
    title: "Unidades de Conservação Ambiental",
  },
  { layerName: "area_quilombola", title: "Territórios Quilombolas" },
  { layerName: "terras_indigenas_homologadas", title: "Terras Indígenas" },
  { layerName: "assentamentos_publicos", title: "Assentamentos (INCRA)" },
  {
    layerName: "aves_migratorias_areas_de_concentracao",
    title: "Aves Migratórias (concentração)",
  },
  {
    layerName: "areas_prioritarias_conservacao_caatinga",
    title: "Áreas Prioritárias – Caatinga",
  },
  {
    layerName: "areas_prioritarias_conservacao_mata_atlantica",
    title: "Áreas Prioritárias – Mata Atlântica",
  },
  {
    layerName: "linhas_de_transmissao_base_existente",
    title: "Linhas de Transmissão em Operação",
  },
  { layerName: "rodovias_federais", title: "Rodovias Federais" },
  { layerName: "rodovias_estaduais", title: "Rodovias Estaduais" },
  { layerName: "corpos_dagua", title: "Corpos d'água" },
  { layerName: "rios", title: "Rios" },
  { layerName: "sitios_arqueologicos", title: "Sítios Arqueológicos" },
  { layerName: "cavernas_pe", title: "Cavernas" },
];

const MASK_COLORS = [
  "#7c3aed",
  "#dc2626",
  "#059669",
  "#d97706",
  "#0284c7",
  "#be185d",
  "#0891b2",
  "#65a30d",
];

const INFRA_LAYERS_CONFIG: Pick<
  InfraLayer,
  "layerName" | "title" | "color" | "minDistKm" | "maxDistKm"
>[] = [
  {
    layerName: "linhas_de_transmissao_base_existente",
    title: "Linhas de Transmissão",
    color: "#f59e0b",
    minDistKm: 0,
    maxDistKm: 10,
  },
  {
    layerName: "LTS-neoenergia",
    title: "Linhas de Distribuição (Neoenergia)",
    color: "#0ea5e9",
    minDistKm: 0,
    maxDistKm: 5,
  },
  {
    layerName: "subestacoes_base_existente",
    title: "Subestações de Transmissão",
    color: "#6366f1",
    minDistKm: 0,
    maxDistKm: 10,
  },
  {
    layerName: "neoenergia_pe_untrat",
    title: "Subestações de Distribuição",
    color: "#a855f7",
    minDistKm: 0,
    maxDistKm: 5,
  },
  // Distanciamento de parques eólicos existentes (regra ANEEL): sem padrão
  // métrico federal, estados costumam exigir 200–1.500 m — controlado à parte
  // no bloco de Ações, não na lista de "Infraestrutura elétrica" (ver EOL_FARM_LAYER_NAME).
  {
    layerName: "eol_base_existente",
    title: "Parques Eólicos Existentes",
    color: "#16a34a",
    minDistKm: 0.35,
    maxDistKm: 0,
  },
];

/** Camada de parques eólicos: exposta como slider de distanciamento no bloco de Ações, não na lista genérica de infraestrutura. */
const EOL_FARM_LAYER_NAME = "eol_base_existente";

const INITIAL_INFRA_LAYERS: InfraLayer[] = INFRA_LAYERS_CONFIG.map((c) => ({
  ...c,
  visible: false,
  geojson: null,
  ringGeom: null,
  fetchedBboxKey: null,
  version: 0,
  loading: false,
  error: null,
}));

function computeInfraRing(
  fc: GeoJSON.FeatureCollection,
  minDistKm: number,
  maxDistKm: number
): GeoJSON.Feature | null {
  if (fc.features.length === 0 || (minDistKm <= 0 && maxDistKm <= 0))
    return null;
  try {
    // Sem distância máxima, a restrição é só o buffer de exclusão em torno da
    // feição (ex.: distanciamento ANEEL de parques eólicos) — não uma faixa entre
    // duas distâncias, então não há "outer" buffer para recortar.
    if (maxDistKm <= 0) return buildEffectiveGeometry(fc, minDistKm);
    const outerBuf = buildEffectiveGeometry(fc, maxDistKm);
    if (!outerBuf) return null;
    if (minDistKm <= 0) return outerBuf;
    const innerBuf = buildEffectiveGeometry(fc, minDistKm);
    if (!innerBuf) return outerBuf;
    return turfDifference(turfFc([outerBuf, innerBuf]) as any) ?? outerBuf;
  } catch {
    return null;
  }
}

const PE_CENTER: [number, number] = [-8.28, -35.07];
const PE_MAX_BOUNDS: [[number, number], [number, number]] = [
  [-10.5, -43.5],
  [-3.5, -32.0],
];
const DEFAULT_MIN_ZOOM = 7;
const POLYGON_TYPES = new Set(["MEASURE_AREA", "DRAW", "CITY"]);

/** Sentinel value for the "Prospectar estado inteiro" option in the Área
 * base select — picking it means "run the propitious-area job against
 * the whole state boundary", not a drawn polygon, so it never matches
 * a real `polygonDrawings` id. */
const STATE_WIDE_OPTION_VALUE = "__STATE_WIDE__";
const WORLD_RING: GeoJSON.Position[] = [
  [-180, -90],
  [180, -90],
  [180, 90],
  [-180, 90],
  [-180, -90],
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function filterBounds(f: MaskFilter): { min?: number; max?: number } {
  switch (f.operator) {
    case "menor":
      return { max: f.value };
    case "maior":
      return { min: f.value };
    case "entre":
      return { min: f.value, max: f.valueMax };
  }
}

function getDrawingGeoJSON(drawing: Drawing): GeoJSON.Feature | null {
  try {
    return (drawing.layer as any).toGeoJSON() as GeoJSON.Feature;
  } catch {
    return null;
  }
}

function buildDonutGeoJSON(geojson: GeoJSON.Feature): GeoJSON.Feature {
  const geom = geojson.geometry;
  const holes: GeoJSON.Position[][] =
    geom.type === "Polygon"
      ? geom.coordinates
      : geom.type === "MultiPolygon"
      ? geom.coordinates.flatMap((poly) => poly)
      : [];
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [WORLD_RING, ...holes] },
  };
}

/** Apply buffer to a FeatureCollection and return a single merged Feature. */
function buildEffectiveGeometry(
  raw: GeoJSON.FeatureCollection,
  bufferKm: number
): GeoJSON.Feature | null {
  const features = raw.features.filter(
    (f): f is GeoJSON.Feature => !!f.geometry
  );
  if (features.length === 0) return null;

  const buffered = features
    .map((f) => {
      try {
        return turfBuffer(f, bufferKm, { units: "kilometers" }) ?? null;
      } catch {
        return null;
      }
    })
    .filter(
      (f): f is GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> =>
        f !== null
    );

  if (buffered.length === 0) return null;
  if (buffered.length === 1) return buffered[0];

  try {
    return turfUnion(turfFc(buffered) as any) ?? buffered[0];
  } catch {
    return buffered[0];
  }
}

async function fetchWFSLayer(
  layerName: string
): Promise<GeoJSON.FeatureCollection> {
  const params = new URLSearchParams({
    service: "WFS",
    version: "2.0.0",
    request: "GetFeature",
    typeNames: `pepe-atlas:${layerName}`,
    outputFormat: "application/json",
  });
  const resp = await fetch(`${GEOSERVER_WFS}?${params}`);
  if (!resp.ok) throw new Error(`WFS error ${resp.status}`);
  return resp.json();
}

/** Fetch vector features from GeoServer WFS within the given bbox. */
async function fetchVectorLayer(
  layerName: string,
  bbox: [number, number, number, number]
): Promise<GeoJSON.FeatureCollection> {
  const [w, s, e, n] = bbox;
  const params = new URLSearchParams({
    service: "WFS",
    version: "2.0.0",
    request: "GetFeature",
    typeNames: `pepe-atlas:${layerName}`,
    outputFormat: "application/json",
    BBOX: `${w},${s},${e},${n},EPSG:4326`,
  });
  const resp = await fetch(`${GEOSERVER_WFS}?${params}`);
  if (!resp.ok) throw new Error(`WFS error ${resp.status}`);
  return resp.json();
}

/** Parse a .kmz or .kml File into a GeoJSON FeatureCollection. */
async function parseKmzFile(file: File): Promise<GeoJSON.FeatureCollection> {
  let kmlText: string;
  if (file.name.toLowerCase().endsWith(".kmz")) {
    const JSZip = (await import("jszip")).default;
    const zip = await JSZip.loadAsync(file);
    const kmlEntry = Object.values(zip.files).find((f) =>
      f.name.toLowerCase().endsWith(".kml")
    );
    if (!kmlEntry) throw new Error("KMZ não contém arquivo KML");
    kmlText = await kmlEntry.async("text");
  } else {
    kmlText = await file.text();
  }
  const dom = new DOMParser().parseFromString(kmlText, "text/xml");
  const { kml: toGeoJSON } = await import("@tmcw/togeojson");
  return toGeoJSON(dom) as GeoJSON.FeatureCollection;
}

function fmtArea(km2: number): string {
  if (km2 >= 1_000) return `${(km2 / 1_000).toFixed(2)} mil km²`;
  return `${km2.toFixed(1)} km²`;
}

function fmtPower(gw: number): string {
  if (gw >= 1_000) return `${(gw / 1_000).toFixed(2)} TW`;
  if (gw >= 1) return `${gw.toFixed(2)} GW`;
  return `${(gw * 1_000).toFixed(1)} MW`;
}

function fmtEnergy(gwh: number): string {
  if (gwh >= 1_000_000) return `${(gwh / 1_000_000).toFixed(2)} PWh`;
  if (gwh >= 1_000) return `${(gwh / 1_000).toFixed(2)} TWh`;
  if (gwh >= 1) return `${gwh.toFixed(1)} GWh`;
  return `${(gwh * 1_000).toFixed(0)} MWh`;
}

function fmtCurrency(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterItem({
  filterKey,
  filter,
  config,
  onToggle,
  onUpdate,
}: {
  filterKey: string;
  filter: MaskFilter;
  config: FilterConfig;
  onToggle: () => void;
  onUpdate: (field: keyof MaskFilter, value: any) => void;
}) {
  return (
    <FilterBlock>
      <FilterHeader onClick={onToggle}>
        <input
          type="checkbox"
          checked={filter.enabled}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          id={`mask-${filterKey}`}
        />
        {/* stopPropagation: o click do label já alterna o checkbox via htmlFor;
            sem isto ele também borbulharia até o header e desfaria o toggle */}
        <label
          htmlFor={`mask-${filterKey}`}
          onClick={(e) => e.stopPropagation()}
        >
          {config.label}
        </label>
      </FilterHeader>

      {filter.enabled && (
        <FilterControls>
          <FilterSelect
            value={filter.operator}
            onChange={(e) => onUpdate("operator", e.target.value as Operator)}
          >
            {(Object.entries(OPERATOR_LABELS) as [Operator, string][]).map(
              ([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              )
            )}
          </FilterSelect>

          <FilterRow>
            <FilterInput
              type="number"
              value={filter.value}
              step={config.step}
              onChange={(e) =>
                onUpdate("value", parseFloat(e.target.value) || 0)
              }
            />
            {filter.operator === "entre" && (
              <>
                <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>e</span>
                <FilterInput
                  type="number"
                  value={filter.valueMax}
                  step={config.step}
                  onChange={(e) =>
                    onUpdate("valueMax", parseFloat(e.target.value) || 0)
                  }
                />
              </>
            )}
            <FilterUnit>{config.unit}</FilterUnit>
          </FilterRow>

          {config.hint && <FilterHint>{config.hint}</FilterHint>}
        </FilterControls>
      )}
    </FilterBlock>
  );
}

function EnergyTypeItem({
  type,
  onRemove,
}: {
  type: EnergyType;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0">
      {type === "eolica" ? (
        <Wind size={16} color="#0C2856" />
      ) : (
        <Sun size={16} color="#0C2856" />
      )}
      <span className="text-xs text-gray-700 font-medium flex-1">
        {ENERGY_TYPE_LABELS[type]}
      </span>
      <button
        onClick={onRemove}
        className="text-gray-300 hover:text-red-400 transition-colors"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function VectorMaskItem({
  mask,
  onRemove,
  onUpdate,
}: {
  mask: VectorMask;
  onRemove: () => void;
  onUpdate: (patch: Partial<VectorMask>) => void;
}) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ background: mask.color }}
        />
        <span className="text-xs text-gray-700 font-medium flex-1 truncate">
          {mask.layerTitle}
        </span>
        {mask.loading && (
          <Loader2 size={12} className="animate-spin text-gray-400" />
        )}
        <button
          onClick={onRemove}
          className="text-gray-300 hover:text-red-400 transition-colors"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {mask.error && (
        <p className="text-xs text-red-400 mb-1.5">{mask.error}</p>
      )}

      {!mask.loading && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Buffer</label>
          <input
            type="number"
            min={0}
            step={0.1}
            value={mask.bufferKm}
            onChange={(e) =>
              onUpdate({ bufferKm: parseFloat(e.target.value) || 0 })
            }
            className="w-16 px-1.5 py-1 border border-gray-200 rounded text-xs text-right"
          />
          <span className="text-xs text-gray-400">km</span>

          <div className="flex rounded border border-gray-200 overflow-hidden ml-auto text-xs">
            {(["inside", "outside"] as const).map((side) => (
              <button
                key={side}
                onClick={() => onUpdate({ inside: side === "inside" })}
                className={[
                  "px-2 py-1 text-xs border-none cursor-pointer transition-colors",
                  (side === "inside") === mask.inside
                    ? "bg-[#0C2856] text-white"
                    : "bg-white text-gray-500",
                ].join(" ")}
              >
                {side === "inside" ? "Dentro" : "Fora"}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KmzMaskItem({
  mask,
  onRemove,
  onUpdate,
}: {
  mask: KmzMask;
  onRemove: () => void;
  onUpdate: (patch: Partial<KmzMask>) => void;
}) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ background: mask.color }}
        />
        <span className="text-xs text-gray-700 font-medium flex-1 truncate">
          {mask.fileName}
        </span>
        <button
          onClick={onRemove}
          className="text-gray-300 hover:text-red-400 transition-colors"
        >
          <Trash2 size={12} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Buffer</label>
        <input
          type="number"
          min={0}
          step={0.1}
          value={mask.bufferKm}
          onChange={(e) =>
            onUpdate({ bufferKm: parseFloat(e.target.value) || 0 })
          }
          className="w-16 px-1.5 py-1 border border-gray-200 rounded text-xs text-right"
        />
        <span className="text-xs text-gray-400">km</span>

        <div className="flex rounded border border-gray-200 overflow-hidden ml-auto text-xs">
          {(["inside", "outside"] as const).map((side) => (
            <button
              key={side}
              onClick={() => onUpdate({ inside: side === "inside" })}
              className={[
                "px-2 py-1 text-xs border-none cursor-pointer transition-colors",
                (side === "inside") === mask.inside
                  ? "bg-[#0C2856] text-white"
                  : "bg-white text-gray-500",
              ].join(" ")}
            >
              {side === "inside" ? "Dentro" : "Fora"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MapBoundsTracker — fires callback on moveend/zoomend
// ---------------------------------------------------------------------------

function MapBoundsTracker({
  onChange,
}: {
  onChange: (bbox: [number, number, number, number]) => void;
}) {
  const map = useMap();
  const cbRef = useRef(onChange);
  cbRef.current = onChange;

  useEffect(() => {
    const update = () => {
      const b = map.getBounds();
      cbRef.current([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    };
    update();
    map.on("moveend", update);
    map.on("zoomend", update);
    return () => {
      map.off("moveend", update);
      map.off("zoomend", update);
    };
  }, [map]);

  return null;
}

// SidebarResizeHandler — Leaflet caches its container size at init, so
// collapsing/expanding the sidebar (a CSS width transition) leaves the map
// showing gray gaps until something tells it to remeasure.
// ---------------------------------------------------------------------------

function SidebarResizeHandler({ collapsed }: { collapsed: boolean }) {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 220);
    return () => clearTimeout(timer);
  }, [map, collapsed]);
  return null;
}

// FitBounds — pans and zooms the map to a GeoJSON feature when it changes
// ---------------------------------------------------------------------------

function FitBounds({ geojson }: { geojson: GeoJSON.Feature | null }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson) return;
    const [w, s, e, n] = turfBbox(geojson) as [number, number, number, number];
    map.fitBounds(
      [
        [s, w],
        [n, e],
      ],
      { padding: [40, 40], maxZoom: 14 }
    );
  }, [geojson, map]);
  return null;
}

// MaxBoundsController — once an area is selected, restricts panning to a
// 100km buffer around it instead of the full-state bounds, and raises
// minZoom so the user can't zoom out past that buffer either (setMaxBounds
// alone only constrains panning, not zoom level). Reverts to the
// state-wide bounds/zoom when nothing is selected.
// ---------------------------------------------------------------------------

function MaxBoundsController({ geojson }: { geojson: GeoJSON.Feature | null }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson) {
      map.setMaxBounds(PE_MAX_BOUNDS);
      map.setMinZoom(DEFAULT_MIN_ZOOM);
      return;
    }
    try {
      const buffered = turfBuffer(geojson, 100, { units: "kilometers" });
      if (!buffered) {
        map.setMaxBounds(PE_MAX_BOUNDS);
        map.setMinZoom(DEFAULT_MIN_ZOOM);
        return;
      }
      const [w, s, e, n] = turfBbox(buffered) as [number, number, number, number];
      const bounds: [[number, number], [number, number]] = [
        [s, w],
        [n, e],
      ];
      map.setMaxBounds(bounds);
      const fitZoom = map.getBoundsZoom(L.latLngBounds(bounds));
      map.setMinZoom(Math.max(DEFAULT_MIN_ZOOM, fitZoom));
    } catch {
      map.setMaxBounds(PE_MAX_BOUNDS);
      map.setMinZoom(DEFAULT_MIN_ZOOM);
    }
  }, [geojson, map]);
  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProjecaoCustomizada() {
  const navigate = useNavigate();
  const { drawings } = useRulerModalContext();
  const { canViewLayer } = useLayerPermissions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [energyTypes, setEnergyTypes] = useState<EnergyType[]>([]);
  const [filters, setFilters] =
    useState<Record<string, MaskFilter>>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<Record<string, MaskFilter>>(INITIAL_FILTERS);

  // Wind turbine parameters
  const [turbineModel, setTurbineModel] = useState<
    "E126" | "AW125" | "generic"
  >("generic");
  const [useWeibull, setUseWeibull] = useState(false);

  const [vectorMasks, setVectorMasks] = useState<VectorMask[]>([]);

  const [kmzMasks, setKmzMasks] = useState<KmzMask[]>([]);
  const [kmzError, setKmzError] = useState<string | null>(null);
  const kmzInputRef = useRef<HTMLInputElement>(null);

  // Keyed by energy type: single-energy modes only ever populate one key, "geral"
  // populates both, since /custom-potential is called once per energy type but
  // shares the same restrictions (raster + vector), so total/excluded/propitious
  // areas match across the two — only installable_gw/annual_energy_gwh/financial differ.
  const [calcResults, setCalcResults] = useState<
    Record<"eolica" | "solar", CalcResult | null>
  >({
    eolica: null,
    solar: null,
  });
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);
  const [stateWideLoading, setStateWideLoading] = useState(false);

  // Financial analysis inputs — all three required for the backend to compute `financial`
  const [annualConsumptionKwh, setAnnualConsumptionKwh] = useState<string>("");
  const [tariffRsKwh, setTariffRsKwh] = useState<string>("");
  const [investmentRs, setInvestmentRs] = useState<string>("");

  const [propitiousGeojson, setPropitiousGeojson] =
    useState<GeoJSON.Feature | null>(null);
  const [propitiousLoading, setPropitiousLoading] = useState(false);
  const [propitiousError, setPropitiousError] = useState<string | null>(null);

  const [reportData, setReportData] = useState<ProjecaoReportData | null>(
    null
  );
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);

  const [basemapId, setBasemapId] = useState<BasemapId>(DEFAULT_TILE);
  const [infraLayers, setInfraLayers] =
    useState<InfraLayer[]>(INITIAL_INFRA_LAYERS);
  const [estadoPeGeojson, setEstadoPeGeojson] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const [mapBbox, setMapBbox] = useState<
    [number, number, number, number] | null
  >(null);

  const infraLayersRef = useRef(infraLayers);
  infraLayersRef.current = infraLayers;
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // O refetch por viewport é sequencial (uma request por camada), então uma
  // nova movimentação do mapa precisa abortar o loop anterior: sem isso ele
  // continuaria gravando o geojson da bbox antiga por cima da mais recente.
  const viewportFetchGenRef = useRef(0);

  useEffect(() => {
    fetchWFSLayer("estado_pe")
      .then(setEstadoPeGeojson)
      .catch(() => {});
  }, []);

  // Re-fetch all visible infra layers 500 ms after the map stops moving
  useEffect(() => {
    if (!mapBbox) return;
    const visibleNames = infraLayersRef.current
      .filter((l) => l.visible)
      .map((l) => l.layerName);
    if (visibleNames.length === 0) return;

    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    const generation = ++viewportFetchGenRef.current;
    refetchTimerRef.current = setTimeout(async () => {
      for (const layerName of visibleNames) {
        if (generation !== viewportFetchGenRef.current) return;
        setInfraLayers((prev) =>
          prev.map((l) =>
            l.layerName === layerName ? { ...l, loading: true } : l
          )
        );
        try {
          const fc = await fetchVectorLayer(layerName, mapBbox);
          if (generation !== viewportFetchGenRef.current) return;
          setInfraLayers((prev) =>
            prev.map((l) => {
              if (l.layerName !== layerName) return l;
              return {
                ...l,
                geojson: fc,
                ringGeom: computeInfraRing(fc, l.minDistKm, l.maxDistKm),
                fetchedBboxKey: mapBbox.join(","),
                version: l.version + 1,
                loading: false,
              };
            })
          );
        } catch {
          if (generation !== viewportFetchGenRef.current) return;
          setInfraLayers((prev) =>
            prev.map((l) =>
              l.layerName === layerName ? { ...l, loading: false } : l
            )
          );
        }
      }
    }, 500);

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [mapBbox]);

  const polygonDrawings = useMemo(
    () =>
      drawings.filter((d) => {
        if (!POLYGON_TYPES.has(d.type)) return false;
        // DRAW (desenho livre) gera linha/ponto — só polígonos servem de área base
        const geomType = getDrawingGeoJSON(d)?.geometry.type;
        return geomType === "Polygon" || geomType === "MultiPolygon";
      }),
    [drawings]
  );

  // Invalida os resultados exibidos E qualquer requisição em voo: as respostas de
  // handleCalculate/handleGeneratePropitious conferem o epoch antes de gravar
  // estado, senão um POST antigo sobrescreveria a seleção/configuração atual.
  // Epochs separados porque turbina/Weibull afetam só o cálculo numérico.
  const calcEpochRef = useRef(0);
  const propitiousEpochRef = useRef(0);
  const invalidateCalcResult = () => {
    calcEpochRef.current += 1;
    setCalcResults({ eolica: null, solar: null });
    // O erro pertence à configuração antiga; mantê-lo na tela o colaria a uma
    // seleção que não o produziu.
    setCalcError(null);
  };
  const invalidateResults = () => {
    invalidateCalcResult();
    propitiousEpochRef.current += 1;
    setPropitiousGeojson(null);
    setPropitiousError(null);
  };

  const selectedDrawing =
    polygonDrawings.find((d) => d.id === selectedId) ?? null;

  const selectedGeoJSON = useMemo(
    () => (selectedDrawing ? getDrawingGeoJSON(selectedDrawing) : null),
    [selectedDrawing]
  );

  // Valor numérico e texto exibido andam juntos, mas separados: o relatório
  // consome o número direto, sem ter de reinterpretar a string formatada.
  const drawnAreaKm2 = selectedGeoJSON
    ? turfArea(selectedGeoJSON) / 1_000_000
    : null;
  const drawnAreaKm2Label =
    drawnAreaKm2 !== null ? drawnAreaKm2.toFixed(2) : null;

  // Single-feature state boundary, used as the "area" for the whole-state
  // background job (no drawn polygon needed). estado_pe normally comes back
  // as one feature; union defensively in case it's ever split in GeoServer.
  const stateBoundaryFeature = useMemo((): GeoJSON.Feature | null => {
    const features = estadoPeGeojson?.features ?? [];
    if (features.length === 0) return null;
    if (features.length === 1) return features[0];
    try {
      return features.reduce(
        (acc, f) => turfUnion(turfFc([acc, f]) as any) ?? acc
      );
    } catch {
      return features[0];
    }
  }, [estadoPeGeojson]);

  const isStateWide = selectedId === STATE_WIDE_OPTION_VALUE;

  // Área sobre a qual as restrições são configuradas: o polígono desenhado ou,
  // no modo "estado inteiro", o próprio limite estadual. As seções de máscara
  // vetorial e de infraestrutura dependem dela — sem isso o job estadual seria
  // sempre enfileirado sem nenhuma restrição vetorial, apesar de o botão
  // prometer aplicá-las.
  const baseAreaFeature = isStateWide ? stateBoundaryFeature : selectedGeoJSON;

  const areaBbox = useMemo(
    (): [number, number, number, number] | undefined =>
      baseAreaFeature
        ? (turfBbox(baseAreaFeature) as [number, number, number, number])
        : undefined,
    [baseAreaFeature]
  );

  // Ordered union of the raster filters relevant to the currently added energy
  // types — "declividade" doesn't depend on energy type at all, so it's
  // always available; "vento"/"ghi" only join in once their energy is added.
  const activeFilterKeys = [
    ...(energyTypes.includes("eolica") ? ["vento"] : []),
    ...(energyTypes.includes("solar") ? ["ghi"] : []),
    "declividade",
  ];
  const hasActiveMasks =
    activeFilterKeys.some((k) => appliedFilters[k].enabled) ||
    vectorMasks.some((m) => m.effectiveGeometry !== null) ||
    kmzMasks.some((m) => m.effectiveGeometry !== null) ||
    infraLayers.some((l) => l.visible && (l.minDistKm > 0 || l.maxDistKm > 0));

  // Threshold de vento: com o filtro "menor que X" ativo, propício é >= X.
  // Só vale para eólica — `removeEnergyType` já restaura filters.vento, mas o
  // gate evita que o payload dependa dessa limpeza.
  const windSpeedThreshold =
    energyTypes.includes("eolica") &&
    filters.vento.enabled &&
    filters.vento.operator === "menor"
      ? filters.vento.value
      : 6.0;

  const hasPendingChanges =
    JSON.stringify(activeFilterKeys.map((k) => filters[k])) !==
    JSON.stringify(activeFilterKeys.map((k) => appliedFilters[k]));

  const resultEnergyTypes = energyTypes;
  const resultsToShow = resultEnergyTypes
    .map((t) => calcResults[t])
    .filter((r): r is CalcResult => r !== null);

  // ── Mask colour assignment ──────────────────────────────────────────────
  const nextColor = () =>
    MASK_COLORS[(vectorMasks.length + kmzMasks.length) % MASK_COLORS.length];

  // ── Raster filter handlers ───────────────────────────────────────────────
  const toggleFilter = (key: string) =>
    setFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled },
    }));

  const updateFilter = (key: string, field: keyof MaskFilter, value: any) =>
    setFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));

  // Adding the first energy re-seeds the filters with that energy's declividade
  // default (mirrors the old single-select behaviour). Adding a second energy on
  // top of an existing one leaves the filters as the user configured them — it
  // only exposes the new energy's own raster filter (vento/ghi) via activeFilterKeys.
  // Declividade is selectable before any energy type is chosen, so if the user
  // already enabled/customized it, that setup is kept instead of being reset.
  const addEnergyType = (type: EnergyType) => {
    if (energyTypes.includes(type)) return;

    if (energyTypes.length === 0) {
      const next = {
        ...INITIAL_FILTERS,
        declividade: filters.declividade.enabled
          ? filters.declividade
          : { ...INITIAL_FILTERS.declividade, value: DECLIV_DEFAULT[type] },
      };
      setFilters(next);
      setAppliedFilters(next);
    }

    setEnergyTypes((prev) => [...prev, type]);
    invalidateResults();
  };

  const removeEnergyType = (type: EnergyType) => {
    const filterKey = RASTER_FILTER_BY_ENERGY[type];
    setFilters((prev) => ({ ...prev, [filterKey]: INITIAL_FILTERS[filterKey] }));
    setAppliedFilters((prev) => ({
      ...prev,
      [filterKey]: INITIAL_FILTERS[filterKey],
    }));
    setEnergyTypes((prev) => prev.filter((t) => t !== type));
    invalidateResults();
  };

  const handleApply = () => {
    setAppliedFilters({ ...filters });
    invalidateResults();
  };

  // ── Vector mask handlers ─────────────────────────────────────────────────
  const addVectorMask = async (layerName: string) => {
    const meta = MASKABLE_LAYERS.find((l) => l.layerName === layerName);
    if (!meta || !baseAreaFeature) return;
    if (vectorMasks.some((m) => m.layerName === layerName)) return;

    const id = `v-${Date.now()}`;
    setVectorMasks((prev) => [
      ...prev,
      {
        id,
        layerName,
        layerTitle: meta.title,
        color: nextColor(),
        bufferKm: 0,
        inside: true,
        rawGeometry: null,
        effectiveGeometry: null,
        loading: true,
        error: null,
      },
    ]);
    invalidateResults();

    try {
      // Bbox expandida para que feições vizinhas à área ainda entrem no buffer
      let bbox: [number, number, number, number];
      try {
        const expanded = turfBuffer(baseAreaFeature, 10, {
          units: "kilometers",
        });
        bbox = turfBbox(expanded ?? baseAreaFeature) as [
          number,
          number,
          number,
          number
        ];
      } catch {
        bbox = turfBbox(baseAreaFeature) as [number, number, number, number];
      }
      const fc = await fetchVectorLayer(layerName, bbox);
      setVectorMasks((prev) =>
        prev.map((m) =>
          m.id === id
            ? {
                ...m,
                rawGeometry: fc,
                effectiveGeometry: buildEffectiveGeometry(fc, m.bufferKm),
                loading: false,
                error:
                  fc.features.length === 0
                    ? "Nenhuma feição encontrada próxima à área"
                    : null,
              }
            : m
        )
      );
    } catch (err: any) {
      setVectorMasks((prev) =>
        prev.map((m) =>
          m.id === id
            ? { ...m, loading: false, error: String(err?.message ?? err) }
            : m
        )
      );
    }
  };

  const updateVectorMask = (id: string, patch: Partial<VectorMask>) => {
    setVectorMasks((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        const updated = { ...m, ...patch };
        // Recompute effectiveGeometry when buffer or raw geometry changes
        if (
          (patch.bufferKm !== undefined || patch.rawGeometry !== undefined) &&
          updated.rawGeometry
        ) {
          updated.effectiveGeometry = buildEffectiveGeometry(
            updated.rawGeometry,
            updated.bufferKm
          );
        }
        return updated;
      })
    );
    invalidateResults();
  };

  const removeVectorMask = (id: string) => {
    setVectorMasks((prev) => prev.filter((m) => m.id !== id));
    invalidateResults();
  };

  // ── KMZ mask handlers ────────────────────────────────────────────────────
  const handleKmzUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setKmzError(null);

    try {
      const fc = await parseKmzFile(file);
      const eff = buildEffectiveGeometry(fc, 0);
      setKmzMasks((prev) => [
        ...prev,
        {
          id: `k-${Date.now()}`,
          fileName: file.name,
          color: nextColor(),
          bufferKm: 0,
          inside: true,
          rawGeometry: fc,
          effectiveGeometry: eff,
        },
      ]);
      invalidateResults();
    } catch (err: any) {
      setKmzError(String(err?.message ?? err));
    }
  };

  const updateKmzMask = (id: string, patch: Partial<KmzMask>) => {
    setKmzMasks((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        const updated = { ...m, ...patch };
        if (patch.bufferKm !== undefined) {
          updated.effectiveGeometry = buildEffectiveGeometry(
            updated.rawGeometry,
            updated.bufferKm
          );
        }
        return updated;
      })
    );
    invalidateResults();
  };

  const removeKmzMask = (id: string) => {
    setKmzMasks((prev) => prev.filter((m) => m.id !== id));
    invalidateResults();
  };

  // ── Infra layer handlers ─────────────────────────────────────────────────
  const toggleInfraLayer = async (layerName: string) => {
    const layer = infraLayers.find((l) => l.layerName === layerName);
    if (!layer || !canViewLayer(layerName)) return;

    // Visibility feeds buildVectorRestrictionGeometries, so both results go stale
    invalidateResults();

    if (layer.visible) {
      setInfraLayers((prev) =>
        prev.map((l) =>
          l.layerName === layerName ? { ...l, visible: false } : l
        )
      );
      return;
    }

    // Enable, refazendo o fetch se nunca carregou ou se o mapa se moveu enquanto
    // a camada esteve oculta (o effect de viewport só atualiza camadas visíveis)
    const bboxKey = mapBbox ? mapBbox.join(",") : null;
    const needsFetch =
      !!mapBbox && (!layer.geojson || layer.fetchedBboxKey !== bboxKey);

    setInfraLayers((prev) =>
      prev.map((l) =>
        l.layerName === layerName
          ? { ...l, visible: true, loading: needsFetch, error: null }
          : l
      )
    );

    if (!needsFetch || !mapBbox) return;

    try {
      const fc = await fetchVectorLayer(layerName, mapBbox);
      setInfraLayers((prev) =>
        prev.map((l) => {
          if (l.layerName !== layerName) return l;
          return {
            ...l,
            geojson: fc,
            ringGeom: computeInfraRing(fc, l.minDistKm, l.maxDistKm),
            fetchedBboxKey: bboxKey,
            version: l.version + 1,
            loading: false,
          };
        })
      );
    } catch (err: any) {
      setInfraLayers((prev) =>
        prev.map((l) =>
          l.layerName === layerName
            ? {
                ...l,
                loading: false,
                error: String(err?.message ?? err),
                visible: false,
              }
            : l
        )
      );
    }
  };

  const updateInfraLayer = (layerName: string, patch: Partial<InfraLayer>) => {
    setInfraLayers((prev) =>
      prev.map((l) => {
        if (l.layerName !== layerName) return l;
        const updated = { ...l, ...patch };
        // Recompute ring whenever distances or geojson change
        if (
          updated.geojson &&
          (patch.minDistKm !== undefined || patch.maxDistKm !== undefined)
        ) {
          updated.ringGeom = computeInfraRing(
            updated.geojson,
            updated.minDistKm,
            updated.maxDistKm
          );
        }
        return updated;
      })
    );
    invalidateResults();
  };

  // ── Restriction geometries (shared) ──────────────────────────────────────
  // Builds the exclusion zones from vector/KMZ masks and infra distance rings as
  // GeoJSON geometries. Used by both the numeric calculation and the propitious-area
  // vector layer so the two endpoints see exactly the same restrictions — the backend
  // unions them with the raster restrictions before measuring, which avoids the old
  // double-subtraction where a vector mask overlapped a raster restriction.

  // Infra features for the calculation are fetched by the selected area's bbox
  // expanded by the layer's largest distance — NOT by the map viewport, which is
  // only used for display. Features outside the area but within maxDistKm still
  // affect the "beyond maxDist" restriction, hence the expansion. Cached per
  // layer+bbox so Calculate and Generate-propitious don't refetch.
  const infraCalcCacheRef = useRef<Map<string, GeoJSON.FeatureCollection>>(
    new Map()
  );

  const fetchInfraForArea = async (
    layer: InfraLayer,
    area: GeoJSON.Feature
  ): Promise<GeoJSON.FeatureCollection> => {
    const padKm = Math.max(layer.minDistKm, layer.maxDistKm, 0);
    let bbox: [number, number, number, number];
    try {
      const expanded =
        padKm > 0 ? turfBuffer(area, padKm, { units: "kilometers" }) : null;
      bbox = turfBbox(expanded ?? area) as [number, number, number, number];
    } catch {
      bbox = turfBbox(area) as [number, number, number, number];
    }
    const key = `${layer.layerName}|${bbox.join(",")}`;
    const cached = infraCalcCacheRef.current.get(key);
    if (cached) return cached;
    try {
      const fc = await fetchVectorLayer(layer.layerName, bbox);
      infraCalcCacheRef.current.set(key, fc);
      return fc;
    } catch {
      // Fall back to the viewport-cached data rather than silently dropping the restriction
      if (layer.geojson) return layer.geojson;
      // Sem dado nenhum não dá para distinguir "sem infraestrutura no raio" de
      // falha de rede — aborta o cálculo em vez de zerar ou superestimar o potencial
      throw new Error(
        `Falha ao buscar a camada "${layer.title}" no GeoServer. Tente novamente.`
      );
    }
  };

  // basePolygon is the area restrictions get clipped to — the drawn area for
  // the interactive calculation, or the whole-state boundary for the
  // background job (see handleGenerateStateWide). Vector/KMZ mask
  // effectiveGeometry is independent of it (only used to search nearby
  // features), so the same restriction-building logic works for both.
  const buildVectorRestrictionGeometries = async (
    basePolygon: GeoJSON.Feature
  ): Promise<GeoJSON.Geometry[]> => {
    const geometries: GeoJSON.Geometry[] = [];

    const allMasks = [
      ...vectorMasks.filter((m) => m.effectiveGeometry),
      ...kmzMasks.filter((m) => m.effectiveGeometry),
    ] as Array<{ effectiveGeometry: GeoJSON.Feature; inside: boolean }>;

    for (const mask of allMasks) {
      try {
        if (mask.inside) {
          geometries.push(mask.effectiveGeometry.geometry);
        } else {
          const diff = turfDifference(
            turfFc([basePolygon, mask.effectiveGeometry]) as any
          );
          if (diff) geometries.push(diff.geometry);
        }
      } catch {}
    }

    for (const layer of infraLayers) {
      if (!layer.visible) continue;
      const fc = await fetchInfraForArea(layer, basePolygon);
      if (fc.features.length === 0) {
        // A bbox da busca é expandida por maxDistKm, então resultado vazio
        // significa que nenhuma feição está a menos de maxDist de qualquer
        // ponto da área: toda ela fica além da distância máxima.
        if (layer.maxDistKm > 0) geometries.push(basePolygon.geometry);
        continue;
      }
      try {
        // Restriction 1: faixa de servidão (inside minDist buffer)
        if (layer.minDistKm > 0) {
          const minBuf = buildEffectiveGeometry(fc, layer.minDistKm);
          if (minBuf) geometries.push(minBuf.geometry);
        }
        // Restriction 2: beyond maxDist (outside maxDist buffer)
        if (layer.maxDistKm > 0) {
          const maxBuf = buildEffectiveGeometry(fc, layer.maxDistKm);
          if (maxBuf) {
            const beyond = turfDifference(
              turfFc([basePolygon, maxBuf]) as any
            );
            if (beyond) geometries.push(beyond.geometry);
          }
        }
      } catch {}
    }

    return geometries;
  };

  // ── Calculate potential ──────────────────────────────────────────────────
  const handleCalculate = async () => {
    if (!selectedGeoJSON || energyTypes.length === 0) return;

    // Aplica automaticamente alterações pendentes do painel antes de calcular,
    // para o payload nunca divergir do que o usuário está vendo
    setAppliedFilters({ ...filters });

    const activeRasterFilters = activeFilterKeys
      .filter((k) => filters[k].enabled)
      .map((k) => {
        const { min, max } = filterBounds(filters[k]);
        return {
          layer_key: k,
          min_val: min ?? null,
          max_val: max ?? null,
        };
      });

    setCalcLoading(true);
    setCalcError(null);
    setCalcResults({ eolica: null, solar: null });

    // With 2+ energies added, we need each one's potential over the same restricted
    // area: /custom-potential only ever prices one energy per call, so it's called
    // once per added type with identical restrictions — total/excluded/propitious
    // areas match between responses, only installable_gw/annual_energy_gwh/financial differ.
    const typesToCalc = energyTypes;

    // Se área/energia/filtros/máscaras mudarem enquanto o POST está em voo, o
    // epoch é bumpado e esta resposta é descartada em vez de sobrescrever o
    // estado da configuração nova
    const epoch = calcEpochRef.current;
    try {
      const vectorRestrictionGeometries =
        await buildVectorRestrictionGeometries(selectedGeoJSON);
      // allSettled: com duas energias, a falha de uma não pode descartar o
      // resultado já obtido da outra — mostra o que deu certo e reporta só o
      // que falhou.
      const settled = await Promise.allSettled(
        typesToCalc.map((t) =>
          apiService
            .post<CalcResult>("/api/v1/tiles/custom-potential", {
              polygon: selectedGeoJSON,
              energy_type: t,
              raster_filters: activeRasterFilters,
              vector_restriction_geometries: vectorRestrictionGeometries,
              turbine_model: turbineModel,
              use_weibull: useWeibull,
              wind_speed_threshold: windSpeedThreshold,
              annual_consumption_kwh: annualConsumptionKwh
                ? Number(annualConsumptionKwh)
                : null,
              tariff_rs_kwh: tariffRsKwh ? Number(tariffRsKwh) : null,
              investment_rs: investmentRs ? Number(investmentRs) : null,
            })
            .then(({ data }) => [t, data] as const)
        )
      );
      if (epoch !== calcEpochRef.current) return;

      setCalcResults((prev) => {
        const next = { ...prev };
        for (const result of settled) {
          if (result.status === "fulfilled") {
            const [t, data] = result.value;
            next[t] = data;
          }
        }
        return next;
      });

      const failures = typesToCalc.filter(
        (_, i) => settled[i].status === "rejected"
      );
      if (failures.length > 0) {
        const reason = (settled.find((r) => r.status === "rejected") as
          | PromiseRejectedResult
          | undefined)?.reason;
        setCalcError(
          `Falha ao calcular ${failures
            .map((t) => ENERGY_TYPE_LABELS[t])
            .join(" e ")}: ${String(reason?.message ?? reason)}`
        );
      }
    } catch (err: any) {
      if (epoch === calcEpochRef.current)
        setCalcError(String(err?.message ?? err));
    } finally {
      setCalcLoading(false);
    }
  };

  // ── Generate propitious area vector layer ────────────────────────────────
  const handleGeneratePropitious = async () => {
    // Unlike handleCalculate/handleGenerateStateWide, this hits
    // /api/v1/tiles/propitious-geojson, which is purely geometric (no
    // energy_type in the payload) — so it works without an energy type.
    if (!selectedGeoJSON) return;

    // Mesma sincronização automática do handleCalculate
    setAppliedFilters({ ...filters });

    const activeRasterFilters = activeFilterKeys
      .filter((k) => filters[k].enabled)
      .map((k) => {
        const { min, max } = filterBounds(filters[k]);
        return { layer_key: k, min_val: min ?? null, max_val: max ?? null };
      });

    setPropitiousLoading(true);
    setPropitiousError(null);
    setPropitiousGeojson(null);

    const epoch = propitiousEpochRef.current;
    try {
      // Same restriction zones as the numeric calculation
      const vectorRestrictionGeometries =
        await buildVectorRestrictionGeometries(selectedGeoJSON);
      const { data: feature } = await apiService.post<GeoJSON.Feature>(
        "/api/v1/tiles/propitious-geojson",
        {
          polygon: selectedGeoJSON,
          raster_filters: activeRasterFilters,
          vector_restriction_geometries: vectorRestrictionGeometries,
        }
      );
      if (epoch === propitiousEpochRef.current) setPropitiousGeojson(feature);
    } catch (err: any) {
      if (epoch === propitiousEpochRef.current)
        setPropitiousError(String(err?.message ?? err));
    } finally {
      setPropitiousLoading(false);
    }
  };

  // ── Generate propitious area for the whole state (background job) ───────
  // Same restrictions panel as the interactive calculation, but clipped to
  // the state boundary instead of a drawn polygon, and run server-side as a
  // background job instead of blocking on the response — see AreasPropicias
  // for the resulting job list/status screen.
  const handleGenerateStateWide = async () => {
    if (energyTypes.length === 0 || !stateBoundaryFeature) return;

    setAppliedFilters({ ...filters });

    const activeRasterFilters = activeFilterKeys
      .filter((k) => filters[k].enabled)
      .map((k) => {
        const { min, max } = filterBounds(filters[k]);
        return { layer_key: k, min_val: min ?? null, max_val: max ?? null };
      });

    setStateWideLoading(true);
    try {
      const vectorRestrictionGeometries = await buildVectorRestrictionGeometries(
        stateBoundaryFeature
      );

      const settled = await Promise.allSettled(
        energyTypes.map((t) =>
          createPropitiousAreaJob({
            energy_type: t,
            state_boundary: stateBoundaryFeature,
            raster_filters: activeRasterFilters,
            vector_restriction_geometries: vectorRestrictionGeometries,
            turbine_model: turbineModel,
            use_weibull: useWeibull,
            wind_speed_threshold: windSpeedThreshold,
            annual_consumption_kwh: annualConsumptionKwh
              ? Number(annualConsumptionKwh)
              : null,
            tariff_rs_kwh: tariffRsKwh ? Number(tariffRsKwh) : null,
            investment_rs: investmentRs ? Number(investmentRs) : null,
          })
        )
      );

      // Um job por energia: com falha parcial o usuário precisa saber quais
      // entraram na fila e quais não — um toast de erro genérico esconderia
      // que metade do trabalho já está rodando.
      const queued = energyTypes.filter(
        (_, i) => settled[i].status === "fulfilled"
      );
      const failed = energyTypes.filter(
        (_, i) => settled[i].status === "rejected"
      );
      const label = (types: EnergyType[]) =>
        types.map((t) => ENERGY_TYPE_LABELS[t]).join(" e ");

      if (queued.length > 0) {
        toast(`Cálculo enfileirado: ${label(queued)}`, {
          description:
            failed.length > 0
              ? `Falha ao enfileirar ${label(
                  failed
                )}. Você será avisado por e-mail quando o restante terminar.`
              : "Você será avisado por e-mail quando terminar. Acompanhe o andamento em Áreas Propícias.",
          action: {
            label: "Ver áreas propícias",
            onClick: () => navigate("/areas-propicias"),
          },
        });
      } else {
        const reason = (settled[0] as PromiseRejectedResult | undefined)?.reason;
        toast("Erro ao enfileirar o cálculo", {
          description: String(
            reason?.response?.data?.detail ?? reason?.message ?? reason
          ),
        });
      }
    } catch (err: any) {
      toast("Erro ao enfileirar o cálculo", {
        description: String(err?.response?.data?.detail ?? err?.message ?? err),
      });
    } finally {
      setStateWideLoading(false);
    }
  };

  // ── Generate report (all inputs used to produce the current propitious
  // area, in the same print/PDF format as the platform's other report) ────
  const handleGenerateReport = async () => {
    setReportGenerating(true);
    try {
      let mapImage: string | undefined;
      const mapEl = document.querySelector(".leaflet-container") as HTMLElement | null;
      if (mapEl) {
        try {
          mapImage = await domToPng(mapEl, { scale: 2, backgroundColor: "#ffffff" });
        } catch {
          mapImage = undefined;
        }
      }

      const areaLabel = isStateWide
        ? "Estado inteiro (Pernambuco)"
        : selectedDrawing?.name ?? "Área selecionada";
      const areaKm2 = isStateWide
        ? stateBoundaryFeature
          ? turfArea(stateBoundaryFeature) / 1_000_000
          : null
        : drawnAreaKm2;

      const currentEolFarmLayer =
        infraLayers.find((l) => l.layerName === EOL_FARM_LAYER_NAME) ?? null;

      const data: ProjecaoReportData = {
        metadata: {
          projectName: areaLabel,
          projectDescription:
            energyTypes.length > 0
              ? `Projeção customizada — ${energyTypes
                  .map((t) => ENERGY_TYPE_LABELS[t])
                  .join(" + ")}`
              : "Projeção customizada",
          timestamp: new Date().toISOString(),
        },
        mapImage,
        areaKm2,
        energyTypes,
        turbine: energyTypes.includes("eolica")
          ? {
              label:
                TURBINE_OPTIONS.find((opt) => opt.value === turbineModel)
                  ?.label ?? turbineModel,
              useWeibull,
            }
          : null,
        rasterFilters: activeFilterKeys
          .filter((key) => appliedFilters[key].enabled)
          .map((key) => ({
            key,
            label: FILTER_CONFIG[key].label,
            operator: appliedFilters[key].operator,
            value: appliedFilters[key].value,
            valueMax: appliedFilters[key].valueMax,
            unit: FILTER_CONFIG[key].unit,
          })),
        vectorMasks: vectorMasks.map((m) => ({
          title: m.layerTitle,
          bufferKm: m.bufferKm,
          inside: m.inside,
        })),
        kmzMasks: kmzMasks.map((m) => ({
          fileName: m.fileName,
          bufferKm: m.bufferKm,
          inside: m.inside,
        })),
        infraLayers: infraLayers
          .filter((l) => l.visible && l.layerName !== EOL_FARM_LAYER_NAME)
          .map((l) => ({
            title: l.title,
            minDistKm: l.minDistKm,
            maxDistKm: l.maxDistKm,
          })),
        aneelBufferMinDistKm: currentEolFarmLayer?.visible
          ? currentEolFarmLayer.minDistKm
          : null,
        financialInputs: {
          annualConsumptionKwh: annualConsumptionKwh
            ? Number(annualConsumptionKwh)
            : null,
          tariffRsKwh: tariffRsKwh ? Number(tariffRsKwh) : null,
          investmentRs: investmentRs ? Number(investmentRs) : null,
        },
        results: resultEnergyTypes
          .map((t) => {
            const r = calcResults[t];
            if (!r) return null;
            return {
              energyType: t,
              totalAreaKm2: r.total_area_km2,
              excludedRasterKm2: r.excluded_raster_km2,
              excludedVectorKm2: r.excluded_vector_km2,
              propitiousAreaKm2: r.propitious_area_km2,
              installableGw: r.installable_gw,
              annualEnergyGwh: r.annual_energy_gwh,
              capacityFactor: r.capacity_factor ?? null,
              financial: r.financial ?? null,
            };
          })
          .filter((r): r is NonNullable<typeof r> => r !== null),
      };

      setReportData(data);
      setIsReportOpen(true);
    } finally {
      setReportGenerating(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // Same layer-permission gating as LayersModal: users only see (and can only
  // fetch) the infra layers their profile allows.
  const permittedInfraLayers = infraLayers.filter(
    (l) => l.layerName !== EOL_FARM_LAYER_NAME && canViewLayer(l.layerName)
  );

  // Camada de parques eólicos existentes: controlada por um slider próprio em
  // sua própria seção (distanciamento ANEEL), não na lista de infraestrutura.
  const eolFarmLayer =
    infraLayers.find((l) => l.layerName === EOL_FARM_LAYER_NAME) ?? null;

  const allMasksForMap = [
    ...vectorMasks
      .filter((m) => m.effectiveGeometry)
      .map((m) => ({
        id: m.id,
        color: m.color,
        bufferKm: m.bufferKm,
        geom: m.effectiveGeometry!,
      })),
    ...kmzMasks
      .filter((m) => m.effectiveGeometry)
      .map((m) => ({
        id: m.id,
        color: m.color,
        bufferKm: m.bufferKm,
        geom: m.effectiveGeometry!,
      })),
  ];

  return (
    <>
      <PageWrapper>
        <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarInner>
          <SidebarHeader>Projeção Customizada</SidebarHeader>

          {/* 1. Área base */}
          <SidebarSection>
            <SectionTitle>Área base</SectionTitle>

            <select
              className="w-full py-2 px-3 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-[#0C2856]"
              value={selectedId}
              onChange={(e) => {
                setSelectedId(e.target.value);
                setVectorMasks([]);
                setKmzMasks([]);
                invalidateResults();
                setInfraLayers(INITIAL_INFRA_LAYERS);
              }}
            >
              <option value="">Selecione uma área…</option>
              {polygonDrawings.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.result ? ` — ${d.result}` : ""}
                </option>
              ))}
              <option value={STATE_WIDE_OPTION_VALUE}>
                Prospectar estado inteiro
              </option>
            </select>

            {polygonDrawings.length === 0 &&
              !isStateWide && (
                <p className="text-xs text-gray-400 leading-relaxed mt-2">
                  Nenhuma área desenhada ainda. Desenhe uma área na ferramenta
                  de mapa, ou selecione "Prospectar estado inteiro" para
                  calcular sobre todo o estado.
                </p>
              )}

            {drawnAreaKm2Label && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-0.5">
                  Área selecionada
                </p>
                <p className="text-sm font-semibold text-[#0C2856]">
                  {drawnAreaKm2Label} km²
                </p>
              </div>
            )}

            {isStateWide && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-gray-700 leading-relaxed">
                  Esse cálculo roda em segundo plano e pode levar alguns
                  minutos. Assim que terminar, o resultado aparece na página{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/areas-propicias")}
                    className="underline font-medium text-[#0C2856] bg-transparent border-none p-0 cursor-pointer"
                  >
                    Áreas propícias solicitadas
                  </button>
                  .
                </p>
              </div>
            )}
          </SidebarSection>

          {/* 2. Tipo de energia */}
          <SidebarSection>
            <SectionTitle>Tipo de energia</SectionTitle>

            {energyTypes.map((type) => (
              <EnergyTypeItem
                key={type}
                type={type}
                onRemove={() => removeEnergyType(type)}
              />
            ))}

            {energyTypes.length === 0 && (
              <p className="text-xs text-gray-400 leading-relaxed">
                Adicione eólica, solar, ou as duas para configurar as
                restrições.
              </p>
            )}

            {energyTypes.length > 1 && (
              <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                Restrições combinadas sobre a mesma área — o potencial
                instalável de cada energia é calculado separadamente.
              </p>
            )}

            {energyTypes.length < 2 && (
              <select
                className="mt-2 w-full py-2 px-3 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white focus:outline-none focus:border-[#0C2856]"
                value=""
                onChange={(e) => {
                  if (e.target.value)
                    addEnergyType(e.target.value as EnergyType);
                }}
              >
                <option value="">Adicionar energia…</option>
                {(["eolica", "solar"] as EnergyType[])
                  .filter((t) => !energyTypes.includes(t))
                  .map((t) => (
                    <option key={t} value={t}>
                      {ENERGY_TYPE_LABELS[t]}
                    </option>
                  ))}
              </select>
            )}
          </SidebarSection>

          {/* 3. Parâmetros da turbina (quando eólica está entre as energias adicionadas) */}
          {energyTypes.includes("eolica") && (
            <SidebarSection>
              <SectionTitle>Parâmetros da turbina</SectionTitle>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">Modelo</p>
                  <div className="space-y-1.5">
                    {TURBINE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-start gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="turbineModel"
                          value={opt.value}
                          checked={turbineModel === opt.value}
                          onChange={() => {
                            setTurbineModel(opt.value);
                            invalidateCalcResult();
                          }}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="text-xs font-medium text-gray-700">
                            {opt.label}
                          </span>
                          <br />
                          <span className="text-xs text-gray-400">
                            {opt.detail}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-start gap-2 cursor-pointer pt-1 border-t border-gray-100">
                  <input
                    type="checkbox"
                    checked={useWeibull}
                    onChange={(e) => {
                      setUseWeibull(e.target.checked);
                      invalidateCalcResult();
                    }}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-xs font-medium text-gray-700">
                      Integração Weibull
                    </span>
                    <br />
                    <span className="text-xs text-gray-400">
                      Fator de capacidade calculado pixel a pixel a partir da
                      distribuição de vento (Rayleigh k=2)
                    </span>
                  </span>
                </label>
              </div>
            </SidebarSection>
          )}

          {/* 4. Camadas de máscara raster */}
          <SidebarSection>
            <SectionTitle>Restrições raster</SectionTitle>
            {activeFilterKeys.map((key) => {
              const config =
                key === "declividade"
                  ? {
                      ...FILTER_CONFIG.declividade,
                      hint:
                        energyTypes.length > 1
                          ? DECLIV_HINT_COMBINED
                          : energyTypes.length === 1
                          ? DECLIV_HINT[energyTypes[0]]
                          : FILTER_CONFIG.declividade.hint,
                    }
                  : FILTER_CONFIG[key];
              return (
                <FilterItem
                  key={key}
                  filterKey={key}
                  filter={filters[key]}
                  config={config}
                  onToggle={() => toggleFilter(key)}
                  onUpdate={(field, value) => updateFilter(key, field, value)}
                />
              );
            })}
          </SidebarSection>

          {/* 5. Restrições vetoriais */}
          <SidebarSection>
            <SectionTitle>Restrições vetoriais</SectionTitle>

            {vectorMasks.map((mask) => (
              <VectorMaskItem
                key={mask.id}
                mask={mask}
                onRemove={() => removeVectorMask(mask.id)}
                onUpdate={(patch) => updateVectorMask(mask.id, patch)}
              />
            ))}

            {!baseAreaFeature ? (
              <p className="text-xs text-gray-400">
                Selecione uma área primeiro.
              </p>
            ) : (
              <select
                className="mt-2 w-full py-2 px-3 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white focus:outline-none focus:border-[#0C2856]"
                value=""
                onChange={(e) => {
                  if (e.target.value) addVectorMask(e.target.value);
                }}
              >
                <option value="">Adicionar camada de restrição…</option>
                {MASKABLE_LAYERS.filter(
                  (l) =>
                    canViewLayer(l.layerName) &&
                    !vectorMasks.some((m) => m.layerName === l.layerName)
                ).map((l) => (
                  <option key={l.layerName} value={l.layerName}>
                    {l.title}
                  </option>
                ))}
              </select>
            )}
          </SidebarSection>

          {/* 6. Arquivo KMZ */}
          <SidebarSection>
            <SectionTitle>Arquivo KMZ / KML</SectionTitle>

            {kmzMasks.map((mask) => (
              <KmzMaskItem
                key={mask.id}
                mask={mask}
                onRemove={() => removeKmzMask(mask.id)}
                onUpdate={(patch) => updateKmzMask(mask.id, patch)}
              />
            ))}

            {kmzError && (
              <p className="text-xs text-red-400 mt-1 mb-2">{kmzError}</p>
            )}

            <button
              onClick={() => kmzInputRef.current?.click()}
              className="mt-2 flex items-center gap-1.5 text-xs text-[#0C2856] font-medium cursor-pointer border-none bg-transparent p-0"
            >
              <Upload size={13} />
              Fazer upload de KMZ / KML
            </button>
            <input
              ref={kmzInputRef}
              type="file"
              accept=".kmz,.kml"
              className="hidden"
              onChange={handleKmzUpload}
            />
          </SidebarSection>

          {/* 7. Infraestrutura elétrica */}
          <SidebarSection>
              <SectionTitle>Infraestrutura elétrica</SectionTitle>

              {!areaBbox ? (
                <p className="text-xs text-gray-400">
                  Selecione uma área primeiro.
                </p>
              ) : permittedInfraLayers.length === 0 ? (
                <p className="text-xs text-gray-400">
                  Seu perfil não tem acesso às camadas de infraestrutura.
                </p>
              ) : (
                <div className="space-y-3">
                  {permittedInfraLayers.map((layer) => (
                    <div
                      key={layer.layerName}
                      className="border border-gray-100 rounded-lg p-2.5"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id={`infra-${layer.layerName}`}
                          checked={layer.visible}
                          onChange={() => toggleInfraLayer(layer.layerName)}
                        />
                        <label
                          htmlFor={`infra-${layer.layerName}`}
                          className="flex-1 text-xs font-medium text-gray-700 cursor-pointer flex items-center gap-1.5"
                        >
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ background: layer.color }}
                          />
                          {layer.title}
                          {layer.loading && (
                            <Loader2
                              size={11}
                              className="animate-spin text-gray-400 ml-1"
                            />
                          )}
                        </label>
                      </div>

                      {layer.error && (
                        <p className="text-xs text-red-400 mb-1">
                          {layer.error}
                        </p>
                      )}

                      {layer.visible && !layer.loading && (
                        <div className="space-y-1.5 pl-5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-20">
                              Dist. mínima
                            </span>
                            <input
                              type="number"
                              min={0}
                              step={0.1}
                              value={layer.minDistKm}
                              onChange={(e) =>
                                updateInfraLayer(layer.layerName, {
                                  minDistKm: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-16 px-1.5 py-1 border border-gray-200 rounded text-xs text-right"
                            />
                            <span className="text-xs text-gray-400">km</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-20">
                              Dist. máxima
                            </span>
                            <input
                              type="number"
                              min={0}
                              step={0.5}
                              value={layer.maxDistKm}
                              onChange={(e) =>
                                updateInfraLayer(layer.layerName, {
                                  maxDistKm: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-16 px-1.5 py-1 border border-gray-200 rounded text-xs text-right"
                            />
                            <span className="text-xs text-gray-400">km</span>
                          </div>
                          <p className="text-xs text-gray-400 leading-snug">
                            Propícia = entre {layer.minDistKm} e{" "}
                            {layer.maxDistKm} km
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </SidebarSection>

          {/* Distanciamento ANEEL de parques eólicos existentes */}
          {energyTypes.includes("eolica") &&
            eolFarmLayer &&
            canViewLayer(EOL_FARM_LAYER_NAME) && (
              <SidebarSection>
                <div className="p-2.5 border border-gray-100 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      id="eol-farm-buffer"
                      checked={eolFarmLayer.visible}
                      onChange={() => toggleInfraLayer(EOL_FARM_LAYER_NAME)}
                    />
                    <label
                      htmlFor="eol-farm-buffer"
                      className="flex-1 text-xs font-medium text-gray-700 cursor-pointer flex items-center gap-1.5"
                    >
                      Distanciamento de parques eólicos (ANEEL)
                      {eolFarmLayer.loading && (
                        <Loader2
                          size={11}
                          className="animate-spin text-gray-400"
                        />
                      )}
                    </label>
                  </div>

                  {eolFarmLayer.error && (
                    <p className="text-xs text-red-400 mb-1">
                      {eolFarmLayer.error}
                    </p>
                  )}

                  {eolFarmLayer.visible && !eolFarmLayer.loading && (
                    <div className="pl-1 pt-1">
                      <input
                        type="range"
                        min={200}
                        max={1500}
                        step={50}
                        value={Math.round(eolFarmLayer.minDistKm * 1000)}
                        onChange={(e) =>
                          updateInfraLayer(EOL_FARM_LAYER_NAME, {
                            minDistKm: parseInt(e.target.value, 10) / 1000,
                          })
                        }
                        className="w-full accent-[#16a34a]"
                      />
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">200 m</span>
                        <span className="text-xs font-semibold text-[#0C2856]">
                          {Math.round(eolFarmLayer.minDistKm * 1000)} m
                        </span>
                        <span className="text-xs text-gray-400">
                          1.500 m
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 leading-snug mt-1">
                        A ANEEL não fixa uma distância mínima nacional entre
                        torres eólicas e residências — normas estaduais
                        costumam variar entre 200 e 1.500 m. Essa faixa é
                        excluída da área propícia ao redor dos parques
                        eólicos existentes na área.
                      </p>
                    </div>
                  )}
                </div>
              </SidebarSection>
            )}

          {/* 8. Análise financeira (opcional) */}
          {energyTypes.length > 0 && (
            <SidebarSection>
              <SectionTitle>Análise financeira (opcional)</SectionTitle>
              <p className="text-xs text-gray-400 mb-2 leading-relaxed">
                Preencha os três campos para ver payback, TIR e custo de geração
                junto com o potencial calculado.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-24 shrink-0">
                    Consumo anual
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={100}
                    value={annualConsumptionKwh}
                    onChange={(e) => setAnnualConsumptionKwh(e.target.value)}
                    placeholder="0"
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs text-right"
                  />
                  <span className="text-xs text-gray-400 w-8">kWh</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-24 shrink-0">
                    Tarifa
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={tariffRsKwh}
                    onChange={(e) => setTariffRsKwh(e.target.value)}
                    placeholder="0,00"
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs text-right"
                  />
                  <span className="text-xs text-gray-400 w-8">R$/kWh</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-24 shrink-0">
                    Investimento
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    value={investmentRs}
                    onChange={(e) => setInvestmentRs(e.target.value)}
                    placeholder="0"
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs text-right"
                  />
                  <span className="text-xs text-gray-400 w-8">R$</span>
                </div>
              </div>
            </SidebarSection>
          )}

          {/* 9. Ações */}
          <SidebarSection>
              <div className="flex flex-col gap-4 pt-1">
                {/* Aplicar restrições */}
                <ApplyButton
                  $hasPending={hasPendingChanges}
                  onClick={handleApply}
                >
                  Aplicar restrições
                </ApplyButton>

                {/* Calcular potencial — precisa de um tipo de energia, já que
                    potencial instalável/energia anual são específicos dele */}
                <button
                  onClick={handleCalculate}
                  disabled={
                    calcLoading || !selectedGeoJSON || energyTypes.length === 0
                  }
                  title={
                    energyTypes.length === 0
                      ? "Adicione um tipo de energia para calcular o potencial"
                      : undefined
                  }
                  className={[
                    "w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold border-none transition-colors",
                    calcLoading || !selectedGeoJSON || energyTypes.length === 0
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-[#0C2856] text-white cursor-pointer hover:bg-[#0a2246]",
                  ].join(" ")}
                >
                  {calcLoading && (
                    <Loader2 size={14} className="animate-spin" />
                  )}
                  {calcLoading
                    ? "Calculando…"
                    : energyTypes.length > 1
                    ? "Calcular potenciais (eólico + solar)"
                    : "Calcular potencial"}
                </button>

                {/* Gerar área propícia */}
                <div>
                  <button
                    onClick={handleGeneratePropitious}
                    disabled={propitiousLoading || !selectedGeoJSON}
                    className={[
                      "w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium border transition-colors",
                      propitiousLoading || !selectedGeoJSON
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                        : propitiousGeojson
                        ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100 cursor-pointer"
                        : "bg-white text-[#0C2856] border-[#0C2856] hover:bg-blue-50 cursor-pointer",
                    ].join(" ")}
                  >
                    {propitiousLoading && (
                      <Loader2 size={13} className="animate-spin" />
                    )}
                    {propitiousLoading
                      ? "Gerando…"
                      : propitiousGeojson
                      ? "Área propícia gerada ✓"
                      : "Gerar área propícia (vetorial)"}
                  </button>
                  {propitiousGeojson && (
                    <button
                      onClick={() => setPropitiousGeojson(null)}
                      className="mt-1.5 w-full text-xs text-gray-400 hover:text-red-400 bg-transparent border-none cursor-pointer"
                    >
                      Remover camada
                    </button>
                  )}
                </div>

                {/* Gerar área propícia para o estado inteiro (job em background) —
                    precisa de um tipo de energia, o job calcula potencial por energia */}
                <div>
                  <button
                    onClick={handleGenerateStateWide}
                    disabled={
                      stateWideLoading ||
                      !stateBoundaryFeature ||
                      energyTypes.length === 0
                    }
                    title={
                      energyTypes.length === 0
                        ? "Adicione um tipo de energia para calcular o estado inteiro"
                        : "Aplica os mesmos filtros de raster (declividade/GHI/vento) e máscaras vetoriais/KMZ/infraestrutura, calculados para o estado inteiro em segundo plano"
                    }
                    className={[
                      "w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium border transition-colors",
                      stateWideLoading ||
                      !stateBoundaryFeature ||
                      energyTypes.length === 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                        : "bg-white text-[#0C2856] border-[#0C2856] hover:bg-blue-50 cursor-pointer",
                    ].join(" ")}
                  >
                    {stateWideLoading && (
                      <Loader2 size={13} className="animate-spin" />
                    )}
                    {stateWideLoading
                      ? "Enfileirando…"
                      : "Gerar área propícia para todo o estado"}
                  </button>
                  <button
                    onClick={() => navigate("/areas-propicias")}
                    className="mt-1.5 w-full text-xs text-gray-400 hover:text-[#0C2856] bg-transparent border-none cursor-pointer"
                  >
                    Ver áreas propícias solicitadas
                  </button>
                </div>
              </div>

              {/* Erros */}
              {calcError && (
                <p className="mt-2 text-xs text-red-400">{calcError}</p>
              )}
              {propitiousError && (
                <p className="mt-1 text-xs text-red-400">{propitiousError}</p>
              )}

              {/* Resultados do cálculo */}
              {resultEnergyTypes.length > 0 && resultsToShow.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      {
                        label: "Área total",
                        value: fmtArea(resultsToShow[0].total_area_km2),
                      },
                      {
                        label: "Excluído (raster)",
                        value: fmtArea(resultsToShow[0].excluded_raster_km2),
                      },
                      {
                        label: "Excluído (vetorial)",
                        value: fmtArea(resultsToShow[0].excluded_vector_km2),
                      },
                      {
                        label: "Área propícia",
                        value: fmtArea(resultsToShow[0].propitious_area_km2),
                        highlight: true,
                      },
                    ].map(({ label, value, highlight }) => (
                      <div
                        key={label}
                        className={`p-2 rounded-lg ${
                          highlight ? "bg-[#0C2856] text-white" : "bg-gray-50"
                        }`}
                      >
                        <p
                          className={`text-xs mb-0.5 ${
                            highlight ? "text-blue-200" : "text-gray-500"
                          }`}
                        >
                          {label}
                        </p>
                        <p
                          className={`text-sm font-semibold ${
                            highlight ? "text-white" : "text-[#0C2856]"
                          }`}
                        >
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>

                  {resultEnergyTypes.map((t) => {
                    const result = calcResults[t];
                    if (!result) return null;
                    return (
                      <Fragment key={t}>
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                          <p className="text-xs text-amber-700 font-semibold mb-1.5">
                            {t === "solar"
                              ? "Potencial Solar"
                              : "Potencial Eólico"}
                            {t === "eolica" && (
                              <span className="ml-1.5 font-normal text-amber-600">
                                {
                                  TURBINE_OPTIONS.find(
                                    (opt) => opt.value === turbineModel
                                  )?.label
                                }
                              </span>
                            )}
                          </p>
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Potencial instalável
                              </span>
                              <span className="text-xs font-semibold text-[#0C2856]">
                                {fmtPower(result.installable_gw)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">
                                Energia anual
                              </span>
                              <span className="text-xs font-semibold text-[#0C2856]">
                                {fmtEnergy(result.annual_energy_gwh)}
                              </span>
                            </div>
                            {result.capacity_factor != null && (
                              <div className="flex justify-between items-center pt-1 border-t border-amber-200">
                                <span className="text-xs text-gray-500">
                                  Fator de capacidade
                                  <span className="ml-1 text-gray-400">
                                    (Weibull)
                                  </span>
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {(result.capacity_factor * 100).toFixed(1)} %
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {result.financial && (
                          <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                            <p className="text-xs text-green-700 font-semibold mb-1.5">
                              Análise financeira
                              {resultEnergyTypes.length > 1 && (
                                <span className="ml-1.5 font-normal text-green-600">
                                  ({t === "solar" ? "Solar" : "Eólica"})
                                </span>
                              )}
                            </p>
                            <div className="space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">
                                  Economia no 1º ano
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {fmtCurrency(
                                    result.financial.annual_savings_rs
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">
                                  Autoconsumo
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {result.financial.self_consumption_fraction_pct.toFixed(
                                    1
                                  )}{" "}
                                  %
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">
                                  Payback simples
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {result.financial.payback_years != null
                                    ? `${result.financial.payback_years} anos e ${result.financial.payback_months} meses`
                                    : "> horizonte do projeto"}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">
                                  TIR (20 anos)
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {result.financial.irr_pct != null
                                    ? `${result.financial.irr_pct.toFixed(
                                        2
                                      )} % a.a.`
                                    : "—"}
                                </span>
                              </div>
                              <div className="flex justify-between items-center pt-1 border-t border-green-200">
                                <span className="text-xs text-gray-500">
                                  Custo de geração (LCOE)
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {result.financial.lcoe_rs_kwh != null
                                    ? `R$ ${result.financial.lcoe_rs_kwh.toFixed(
                                        3
                                      )} /kWh`
                                    : "—"}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-500">
                                  CO₂ evitado
                                </span>
                                <span className="text-xs font-semibold text-[#0C2856]">
                                  {(
                                    result.financial.co2_avoided_kg_year / 1000
                                  ).toFixed(1)}{" "}
                                  t/ano
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </Fragment>
                    );
                  })}

                  <p className="text-xs text-gray-400 leading-relaxed">
                    Estimativa baseada em densidades médias de PE.
                    {!hasActiveMasks &&
                      " Nenhuma restrição ativa — área propícia = área base."}
                  </p>

                  <button
                    onClick={handleGenerateReport}
                    disabled={reportGenerating}
                    className={[
                      "w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-xs font-medium border transition-colors",
                      reportGenerating
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                        : "bg-white text-[#0C2856] border-[#0C2856] hover:bg-blue-50 cursor-pointer",
                    ].join(" ")}
                  >
                    {reportGenerating ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <FileText size={13} />
                    )}
                    {reportGenerating ? "Gerando relatório…" : "Gerar relatório"}
                  </button>
                </div>
              )}
          </SidebarSection>
        </SidebarInner>
        </Sidebar>

        <SidebarToggle
          type="button"
          $collapsed={sidebarCollapsed}
          onClick={() => setSidebarCollapsed((v) => !v)}
          title={sidebarCollapsed ? "Mostrar painel" : "Ocultar painel"}
          aria-label={sidebarCollapsed ? "Mostrar painel" : "Ocultar painel"}
        >
          {sidebarCollapsed ? (
            <ChevronRight size={14} />
          ) : (
            <ChevronLeft size={14} />
          )}
        </SidebarToggle>

        <MapWrapper>
          <MapContainer
            zoom={8}
            center={PE_CENTER}
            style={{ width: "100%", height: "100%" }}
            zoomControl={false}
            scrollWheelZoom
            maxBounds={PE_MAX_BOUNDS}
            maxBoundsViscosity={1.0}
            minZoom={7}
            maxZoom={MAP_MAX_ZOOM}
          >
            {(() => {
              const bm =
                BASEMAPS.find((b) => b.id === basemapId) ?? BASEMAPS[0];
              return (
                <TileLayer
                  key={bm.id}
                  url={bm.url}
                  maxZoom={MAP_MAX_ZOOM}
                  maxNativeZoom={bm.maxNativeZoom}
                  attribution={bm.attribution}
                  crossOrigin="anonymous"
                />
              );
            })()}
            <MapBoundsTracker onChange={setMapBbox} />
            <SidebarResizeHandler collapsed={sidebarCollapsed} />

            {estadoPeGeojson && (
              <LeafletGeoJSON
                key="estado-pe"
                data={estadoPeGeojson}
                style={{
                  color: "#0C2856",
                  fillColor: "transparent",
                  fillOpacity: 0,
                  weight: 2,
                  opacity: 0.5,
                  dashArray: "6 3",
                }}
              />
            )}

            <FitBounds geojson={selectedGeoJSON} />
            <MaxBoundsController geojson={selectedGeoJSON} />

            {/* Infra layers — ring (propitious corridor) + raw features */}
            {infraLayers
              .filter((l) => l.visible)
              .map((l) => (
                <Fragment key={l.layerName}>
                  {l.ringGeom && (
                    <LeafletGeoJSON
                      key={`infra-ring-${l.layerName}-${l.version}-${l.minDistKm}-${l.maxDistKm}`}
                      data={l.ringGeom}
                      style={{
                        color: l.color,
                        fillColor: l.color,
                        fillOpacity: 0.15,
                        weight: 1.5,
                        opacity: 0.6,
                        dashArray: "4 4",
                      }}
                    />
                  )}
                  {l.geojson && (
                    <LeafletGeoJSON
                      key={`infra-${l.layerName}-${l.version}`}
                      data={l.geojson}
                      style={{
                        color: l.color,
                        fillColor: l.color,
                        fillOpacity: 0.8,
                        weight: 2,
                        opacity: 1,
                      }}
                      pointToLayer={(_feature, latlng) =>
                        L.circleMarker(latlng, {
                          radius: 5,
                          fillColor: l.color,
                          color: "#fff",
                          weight: 1.5,
                          fillOpacity: 0.9,
                        })
                      }
                    />
                  )}
                </Fragment>
              ))}

            {/* Vector / KMZ restriction layers */}
            {allMasksForMap.map(({ id, color, bufferKm, geom }) => (
              <LeafletGeoJSON
                key={`mask-${id}-${bufferKm}`}
                data={geom}
                style={{
                  color,
                  fillColor: color,
                  fillOpacity: 0.35,
                  weight: 1.5,
                  opacity: 0.8,
                }}
              />
            ))}

            {/* Donut mask — hides everything outside the selected polygon */}
            {selectedGeoJSON && hasActiveMasks && (
              <LeafletGeoJSON
                key={`donut-${selectedId}`}
                data={buildDonutGeoJSON(selectedGeoJSON)}
                style={{
                  stroke: false,
                  color: "transparent",
                  fillColor: "#ffffff",
                  fillOpacity: 0.88,
                  weight: 0,
                }}
              />
            )}

            {/* Propitious area vector layer */}
            {propitiousGeojson && (
              <LeafletGeoJSON
                key={`propitious-${selectedId}`}
                data={propitiousGeojson}
                style={{
                  color: "#16a34a",
                  fillColor: "#22c55e",
                  fillOpacity: 0.35,
                  weight: 2,
                  opacity: 0.9,
                }}
              />
            )}

            {/* Selected polygon outline */}
            {selectedGeoJSON && (
              <LeafletGeoJSON
                key={`outline-${selectedId}`}
                data={selectedGeoJSON}
                style={{
                  color: "#0C2856",
                  fillColor: "#0C2856",
                  fillOpacity: 0.1,
                  weight: 2.5,
                }}
              />
            )}
          </MapContainer>
          <BasemapControl selected={basemapId} onChange={setBasemapId} />
        </MapWrapper>
      </PageWrapper>
      <Footer />

      <ProjecaoReportPreview
        isOpen={isReportOpen}
        reportData={reportData}
        onClose={() => setIsReportOpen(false)}
      />
    </>
  );
}
